import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ChevronLeft, Sparkles, Loader2, AlertTriangle, Check, Pencil, ChevronDown,
  Calendar, Euro, Hash, FileCheck, ListChecks, AlertOctagon, Building2, FileText, CalendarDays,
  FolderPlus, HelpCircle, Volume2, Square, MessagesSquare, Send,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PriorityBadge } from "@/components/PriorityBadge";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["FINANZAMT", "BUERGERAMT", "AUSLAENDERBEHOERDE", "JOBCENTER", "KRANKENKASSE", "OTHER"];

// Map ESVAN language codes to BCP-47 tags so speech is read in the SELECTED language.
const BCP47 = {
  de: "de-DE", en: "en-US", tr: "tr-TR", ar: "ar-SA", ro: "ro-RO", fa: "fa-IR",
  ps: "ps-AF", uk: "uk-UA", pl: "pl-PL", bg: "bg-BG", it: "it-IT", es: "es-ES", fr: "fr-FR",
};

const CAT_ICON = {
  deadline: Calendar,
  amount: Euro,
  reference_number: Hash,
  required_document: FileCheck,
  requested_action: ListChecks,
  consequence: AlertOctagon,
};

function ConfidencePill({ conf }) {
  const { t } = useI18n();
  if (conf == null) return null;
  const pct = Math.round(conf * 100);
  const low = conf < 0.6;
  const mid = conf >= 0.6 && conf < 0.8;
  const style = low
    ? { bg: "#FDF2F0", color: "#C03A2B", border: "#F8C7C0" }
    : mid
    ? { bg: "#FFF8EC", color: "#B36B00", border: "#FFE2B3" }
    : { bg: "#EDF7F2", color: "#1B6943", border: "#B3E2CB" };
  return (
    <span
      data-testid="confidence-pill"
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold"
      style={{ backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}
    >
      {low ? t("analysis.unsure") : `${pct}%`}
    </span>
  );
}

function StatusChip({ status }) {
  const { t } = useI18n();
  const map = {
    PENDING: { label: t("analysis.pending"), bg: "#F1F3F2", color: "#5A625E" },
    CONFIRMED: { label: t("analysis.confirmed"), bg: "#EDF7F2", color: "#1B6943" },
    CORRECTED: { label: t("analysis.corrected"), bg: "#EBF3FF", color: "#1E5299" },
  };
  const s = map[status] || map.PENDING;
  return (
    <span data-testid="item-status-chip" className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const RAW_VALUE_CATS = new Set(["deadline", "amount", "reference_number"]);

function CriticalItem({ item, analysisId, loc }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.confirmed_value || item.ai_value || "");
  const Icon = CAT_ICON[item.category] || FileText;
  // Localized (user-language) rendering of this item. For value-type items (deadline/amount/
  // reference) it's a short descriptive LABEL and the value stays verbatim; for sentence-type
  // items (requested_action/required_document/consequence) it's the full localized meaning.
  const locText = loc?.items?.[item.item_id];
  const rawCat = RAW_VALUE_CATS.has(item.category);
  const labelText = rawCat ? (locText || item.label) : null;
  const primaryValue = rawCat ? item.ai_value : (locText || item.ai_value);

  const mutate = useMutation({
    mutationFn: async (body) =>
      (await api.post(`/analyses/${analysisId}/items/${item.item_id}/confirm`, body)).data,
    onSuccess: (data) => {
      qc.setQueryData(["analysis", data.document_id], data);
      qc.invalidateQueries({ queryKey: ["case-prefill", data.document_id] });
      setEditing(false);
    },
    onError: (err) => toast.error(apiError(err, t)),
  });

  const corrected = item.confirmation_status === "CORRECTED";

  return (
    <div data-testid={`critical-item-${item.item_id}`} className="rounded-2xl border border-[#E2EAE5] bg-white p-3.5">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F0F4F1] text-[#2D5A4C]">
          <Icon size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A9488]">{t(`analysis.cat.${item.category}`)}</p>
            <div className="flex items-center gap-1.5">
              <ConfidencePill conf={item.confidence} />
              <StatusChip status={item.confirmation_status} />
            </div>
          </div>
          {labelText && <p className="mt-0.5 text-xs text-[#7A9488]">{labelText}</p>}

          {corrected ? (
            <div className="mt-1">
              <p className="text-sm font-bold text-[#1A2E26]" data-testid="item-confirmed-value">{item.confirmed_value}</p>
              <p className="text-[11px] text-[#7A9488] line-through">{t("analysis.aiValue")}: {item.ai_value}</p>
            </div>
          ) : (
            <p className="mt-1 text-sm font-bold text-[#1A2E26]" dir="auto" data-testid="item-ai-value">{primaryValue}</p>
          )}

          <button
            data-testid={`item-source-toggle-${item.item_id}`}
            onClick={() => setOpen((o) => !o)}
            className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-[#2D5A4C]"
          >
            <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            {t("analysis.source")} · {t("analysis.page")} {item.page ?? "?"}
          </button>
          {open && (
            <blockquote
              data-testid={`item-source-${item.item_id}`}
              dir="auto"
              className="mt-1.5 border-l-2 border-[#C4DFD2] bg-[#F8FAF8] px-3 py-2 text-xs italic text-[#4A6358]"
            >
              {item.source_fragment || "—"}
            </blockquote>
          )}

          {editing ? (
            <div className="mt-3 space-y-2">
              <Input
                data-testid={`item-correct-input-${item.item_id}`}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={t("analysis.correctPlaceholder")}
                className="h-10 rounded-lg"
              />
              <div className="flex gap-2">
                <Button
                  data-testid={`item-correct-save-${item.item_id}`}
                  onClick={() => mutate.mutate({ status: "CORRECTED", confirmed_value: value })}
                  disabled={mutate.isPending || !value.trim()}
                  className="h-9 rounded-lg bg-[#2D5A4C] text-xs hover:bg-[#214439]"
                >
                  {t("analysis.save")}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="h-9 rounded-lg text-xs">
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3 flex gap-2">
              <button
                data-testid={`item-confirm-${item.item_id}`}
                onClick={() => mutate.mutate({ status: "CONFIRMED" })}
                disabled={mutate.isPending}
                className="flex items-center gap-1 rounded-full border border-[#B3E2CB] bg-[#EDF7F2] px-3 py-1.5 text-xs font-semibold text-[#1B6943] active:scale-95"
              >
                <Check size={13} /> {t("analysis.confirm")}
              </button>
              <button
                data-testid={`item-correct-${item.item_id}`}
                onClick={() => { setValue(item.confirmed_value || item.ai_value || ""); setEditing(true); }}
                className="flex items-center gap-1 rounded-full border border-[#E2EAE5] bg-white px-3 py-1.5 text-xs font-semibold text-[#4A6358] active:scale-95"
              >
                <Pencil size={13} /> {t("analysis.correct")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OverviewRow({ icon: Icon, label, value }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <Icon size={16} className="shrink-0 text-[#7A9488]" />
      <span className="text-xs text-[#7A9488]">{label}</span>
      <span className="ml-auto text-right text-sm font-semibold text-[#1A2E26]" dir="auto">
        {value || <span className="text-[#B0C2B8] font-normal">{t("analysis.notFound")}</span>}
      </span>
    </div>
  );
}

// Human-readable "I got this letter, what do I do?" card — strictly from grounded fields.
function ExplanationCard({ analysis, loc, loading }) {
  const { t, lang } = useI18n();
  const [speaking, setSpeaking] = useState(false);
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
  const items = analysis.critical_items || [];
  const valOf = (it) => (it.confirmation_status === "CORRECTED" ? it.confirmed_value : it.ai_value);
  const byCat = (c) => items.filter((i) => i.category === c);
  const deadlines = byCat("deadline");
  const amounts = byCat("amount");
  const consequences = byCat("consequence");
  const steps = [...byCat("requested_action"), ...byCat("required_document")];
  const ar = analysis.action_required || {};
  const actionAns = ar.value === true ? t("xp.ansYes") : ar.value === false ? t("xp.ansNo") : t("xp.ansUnknown");
  const na = <span className="font-normal text-[#B0C2B8]">{t("analysis.notFound")}</span>;
  const L = loc || {};
  // Localized user-facing text for a critical item (single language; never the German base label).
  const locText = (it) => (loc && loc.items && loc.items[it.item_id]) || valOf(it);
  const actText = L.action_text;

  // Read the LOCALIZED explanation aloud in the currently selected language.
  const buildSpeech = () => {
    const parts = [t("xp.explainTitle")];
    const add = (q, v) => { if (v) parts.push(`${q} ${v}`); };
    add(t("xp.qWhat"), L.document_type || analysis.summary);
    add(t("xp.qWho"), analysis.sender?.value);
    add(t("xp.qWant"), L.sender_wants);
    parts.push(`${t("xp.qAction")} ${actionAns}${actText ? ", " + actText : ""}`);
    if (deadlines.length) add(t("xp.qDeadline"), deadlines.map((it) => [valOf(it), locText(it) !== valOf(it) ? locText(it) : ""].filter(Boolean).join(" ")).join(", "));
    if (amounts.length) add(t("xp.qMoney"), amounts.map((it) => [valOf(it), locText(it) !== valOf(it) ? locText(it) : ""].filter(Boolean).join(" ")).join(", "));
    if (L.amount_warning) parts.push(L.amount_warning);
    if (consequences.length) add(t("xp.qConsequence"), consequences.map(locText).filter(Boolean).join(". "));
    if (steps.length) add(t("xp.qSteps"), steps.map(locText).filter(Boolean).join(". "));
    return parts.join(". ");
  };

  const toggleSpeak = () => {
    if (!ttsSupported) return;
    const synth = window.speechSynthesis;
    if (speaking) { synth.cancel(); setSpeaking(false); return; }
    const tag = BCP47[lang] || lang;
    const voices = synth.getVoices() || [];
    const voice = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(tag.toLowerCase()))
      || voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(lang));
    // Never read text with a wrong-language voice: if voices are known and none match, bail out.
    if (voices.length && !voice) { toast.error(t("xp.ttsUnavailable")); return; }
    synth.cancel();
    const u = new SpeechSynthesisUtterance(buildSpeech());
    u.lang = tag;
    if (voice) u.voice = voice;
    u.onend = () => setSpeaking(false);
    u.onerror = () => setSpeaking(false);
    setSpeaking(true);
    synth.speak(u);
  };

  useEffect(() => () => { if (ttsSupported) window.speechSynthesis.cancel(); }, [ttsSupported]);

  const Row = ({ q, children }) => (
    <div className="border-t border-[#F0F4F1] py-2.5 first:border-t-0 first:pt-0">
      <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A9488]">{q}</p>
      <div className="mt-0.5 text-sm text-[#1A2E26]" dir="auto">{children}</div>
    </div>
  );

  return (
    <section data-testid="analysis-explanation" className="rounded-2xl border border-[#C4DFD2] bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#2D5A4C] text-white">
          <HelpCircle size={16} />
        </div>
        <h2 className="font-display text-base font-extrabold text-[#1A2E26]">{t("xp.explainTitle")}</h2>
        {ttsSupported && (
          <button
            data-testid="tts-play-button"
            onClick={toggleSpeak}
            aria-label={speaking ? t("xp.stop") : t("xp.listen")}
            className="ml-auto flex shrink-0 items-center gap-1 rounded-full border border-[#C4DFD2] bg-[#F2F7F4] px-2.5 py-1 text-xs font-semibold text-[#2D5A4C] transition-colors hover:bg-[#E9F1EC] active:scale-95"
          >
            {speaking ? <Square size={13} /> : <Volume2 size={14} />}
            {speaking ? t("xp.stop") : t("xp.listen")}
          </button>
        )}
      </div>
      <div className="divide-y-0">
        {loading && !loc ? (
          <div className="flex items-center gap-2 py-3 text-sm text-[#7A9488]">
            <Loader2 size={16} className="animate-spin" /> {t("xp.analyzingAuto")}
          </div>
        ) : (
        <>
        <Row q={t("xp.qWhat")}>
          {L.document_type || na}
          {L.document_type_original && L.document_type_original !== L.document_type && (
            <span className="mt-0.5 block text-xs text-[#7A9488]">{t("xp.originalTitle")}: {L.document_type_original}</span>
          )}
        </Row>
        <Row q={t("xp.qWho")}>{analysis.sender?.value || na}</Row>
        <Row q={t("xp.qWant")}>{L.sender_wants || na}</Row>
        <Row q={t("xp.qAction")}>
          <span className="font-semibold">{actionAns}</span>
          {actText && <span className="text-[#4A6358]"> — {actText}</span>}
        </Row>
        {deadlines.length > 0 && (
          <Row q={t("xp.qDeadline")}>
            {deadlines.map((it) => (
              <div key={it.item_id}>
                <span className="font-semibold">{valOf(it)}</span>
                {locText(it) && locText(it) !== valOf(it) && <span className="text-[#7A9488]"> — {locText(it)}</span>}
              </div>
            ))}
          </Row>
        )}
        {amounts.length > 0 && (
          <Row q={t("xp.qMoney")}>
            {amounts.map((it) => (
              <div key={it.item_id}>
                <span className="font-semibold">{valOf(it)}</span>
                {locText(it) && locText(it) !== valOf(it) && <span className="text-[#7A9488]"> — {locText(it)}</span>}
              </div>
            ))}
            {L.amount_warning && (
              <div className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[#FFF8EC] px-2.5 py-1.5 text-xs text-[#B36B00]">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />{L.amount_warning}
              </div>
            )}
          </Row>
        )}
        {consequences.length > 0 && (
          <Row q={t("xp.qConsequence")}>
            <ul className="list-disc space-y-0.5 pl-4">
              {consequences.map((c) => <li key={c.item_id}>{locText(c)}</li>)}
            </ul>
          </Row>
        )}
        {steps.length > 0 && (
          <Row q={t("xp.qSteps")}>
            <ol className="list-decimal space-y-1 pl-4 font-medium">
              {steps.map((s) => <li key={s.item_id}>{locText(s)}</li>)}
            </ol>
          </Row>
        )}
        </>
        )}
      </div>
    </section>
  );
}

// Discreet contextual mini-Q&A: clarify the document in the selected language (max 3 questions).
function MiniQA({ docId }) {
  const { t, lang } = useI18n();
  const storeKey = `esvan_qa_${docId}`;
  const initial = (() => {
    try { return JSON.parse(localStorage.getItem(storeKey)) || {}; } catch { return {}; }
  })();
  const [q, setQ] = useState("");
  const [thread, setThread] = useState(initial.thread || []);
  const [sid, setSid] = useState(initial.sid || null);
  const [remaining, setRemaining] = useState(typeof initial.remaining === "number" ? initial.remaining : 3);
  const [loading, setLoading] = useState(false);

  // Persist the Q&A session per document so a page reload cannot reset the 3-question limit.
  useEffect(() => {
    try { localStorage.setItem(storeKey, JSON.stringify({ sid, remaining, thread })); } catch (e) { /* ignore quota */ }
  }, [storeKey, sid, remaining, thread]);

  const ask = async () => {
    const question = q.trim();
    if (!question || loading || remaining <= 0) return;
    setLoading(true);
    setThread((prev) => [...prev, { role: "user", content: question }]);
    setQ("");
    try {
      const { data } = await api.post(`/documents/${docId}/ask`, { question, language: lang, session_id: sid });
      setSid(data.session_id);
      setRemaining(data.remaining);
      setThread((prev) => [...prev, { role: "assistant", content: data.answer }]);
    } catch (e) {
      toast.error(apiError(e, t));
      setThread((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="mini-qa-section" className="rounded-2xl border border-[#E2EAE5] bg-white p-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#F0F4F1] text-[#2D5A4C]">
          <MessagesSquare size={16} />
        </div>
        <h3 className="font-display text-sm font-bold text-[#1A2E26]">{t("xp.qaTitle")}</h3>
      </div>

      {thread.length > 0 && (
        <div className="mb-3 space-y-2">
          {thread.map((m, i) => (
            <div
              key={i}
              data-testid={m.role === "assistant" ? "mini-qa-answer" : "mini-qa-question"}
              dir="auto"
              className={
                m.role === "assistant"
                  ? "rounded-xl rounded-tl-sm bg-[#F2F7F4] px-3 py-2 text-sm text-[#1A2E26]"
                  : "ml-6 rounded-xl rounded-tr-sm bg-[#2D5A4C] px-3 py-2 text-sm text-white"
              }
            >
              {m.content}
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 py-1 text-xs text-[#7A9488]">
              <Loader2 size={14} className="animate-spin" /> {t("xp.qaThinking")}
            </div>
          )}
        </div>
      )}

      {remaining > 0 ? (
        <>
          <div className="flex items-end gap-2">
            <Input
              data-testid="mini-qa-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder={t("xp.qaPlaceholder")}
              dir="auto"
              className="h-11 flex-1 rounded-xl"
            />
            <Button
              data-testid="mini-qa-send"
              onClick={ask}
              disabled={loading || !q.trim()}
              className="h-11 shrink-0 rounded-xl bg-[#2D5A4C] px-3 hover:bg-[#214439]"
            >
              <Send size={16} />
            </Button>
          </div>
          <p className="mt-1.5 text-[11px] text-[#7A9488]">{t("xp.qaRemaining").replace("{n}", remaining)}</p>
        </>
      ) : (
        <p data-testid="mini-qa-limit" className="rounded-xl bg-[#F1F3F2] px-3 py-2 text-xs text-[#5A625E]">
          {t("xp.qaLimit")}
        </p>
      )}
    </section>
  );
}

// Coherence warning: a page may belong to a different document. User decides (Keep anyway).
function PageWarnings({ warnings }) {
  const { t } = useI18n();
  const [dismissed, setDismissed] = useState(false);
  if (!warnings || !warnings.length || dismissed) return null;
  return (
    <section data-testid="page-warnings" className="rounded-2xl border border-[#F0D9A8] bg-[#FFF8EC] p-4">
      <div className="flex items-start gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#B36B00]" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#8A5200]">{t("xp.mpWarnTitle")}</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs text-[#8A5200]">
            {warnings.map((w, i) => <li key={i}>{t("xp.mpPage").replace("{n}", w.page)}</li>)}
          </ul>
          <button
            data-testid="page-warn-keep"
            onClick={() => setDismissed(true)}
            className="mt-2 rounded-lg border border-[#F0D9A8] bg-white px-3 py-1.5 text-xs font-semibold text-[#8A5200]"
          >
            {t("xp.mpWarnKeep")}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function DocumentAnalysis() {
  const { t, lang } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const auto = params.get("auto");
  const triggered = useRef(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [caseTitle, setCaseTitle] = useState("");
  const [category, setCategory] = useState("OTHER");
  const [creating, setCreating] = useState(false);

  const { data: analysis, isLoading } = useQuery({
    queryKey: ["analysis", id],
    queryFn: async () => {
      try {
        return (await api.get(`/documents/${id}/analysis`)).data;
      } catch (e) {
        if (e.response?.status === 404) return null;
        throw e;
      }
    },
    retry: false,
  });

  const analyzeMutation = useMutation({
    mutationFn: async () => (await api.post(`/documents/${id}/analyze`, { language: lang })).data,
    onSuccess: (data) => {
      qc.setQueryData(["analysis", id], data);
      qc.invalidateQueries({ queryKey: ["case-prefill", id] });
    },
    onError: (err) => toast.error(apiError(err, t)),
  });

  // Auto-run analysis right after upload (streamlined flow).
  useEffect(() => {
    if (auto && !isLoading && analysis === null && !analyzeMutation.isPending && !triggered.current) {
      triggered.current = true;
      analyzeMutation.mutate();
    }
  }, [auto, isLoading, analysis, analyzeMutation]);

  const { data: prefill } = useQuery({
    queryKey: ["case-prefill", id, lang],
    queryFn: async () => (await api.get(`/documents/${id}/case-prefill`, { params: { language: lang } })).data,
    enabled: !!analysis && !analysis.unreadable,
    retry: false,
  });
  // Localized explanation layer for the CURRENTLY selected language (regenerated per language).
  const { data: loc, isFetching: locLoading } = useQuery({
    queryKey: ["explanation", id, lang],
    queryFn: async () => (await api.get(`/documents/${id}/explanation`, { params: { language: lang } })).data,
    enabled: !!analysis && !analysis.unreadable,
    retry: false,
  });
  useEffect(() => {
    if (prefill) {
      setCaseTitle(prefill.title || "");
      setCategory(prefill.category || "OTHER");
    }
  }, [prefill]);

  const organize = async (attachId) => {
    setCreating(true);
    try {
      const body = attachId
        ? { attach_to_case_id: attachId }
        : { title: caseTitle.trim(), category };
      const { data } = await api.post(`/documents/${id}/create-case`, body);
      qc.invalidateQueries({ queryKey: ["cases"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(t("xp.caseReady"));
      navigate(`/cases/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(apiError(e, t));
      setCreating(false);
    }
  };

  return (
    <div className="relative mx-auto min-h-screen max-w-md border-x border-[#E2EAE5]/60 bg-[#F8FAF8] pb-28 shadow-sm">
      <header className="sticky top-0 z-40 flex items-center gap-2 border-b border-[#E2EAE5] bg-[#F8FAF8]/90 px-4 py-3 backdrop-blur-md">
        <button data-testid="analysis-back" onClick={() => navigate(-1)} className="rounded-full p-1.5 text-[#4A6358] hover:bg-[#F0F4F1]">
          <ChevronLeft size={22} />
        </button>
        <h1 className="font-display text-base font-bold text-[#1A2E26]">{t("analysis.title")}</h1>
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </header>

      <main className="esv-fade px-4 pt-5">
        {isLoading ? (
          <div className="space-y-3">
            <div className="h-24 animate-pulse rounded-2xl bg-[#F0F4F1]" />
            <div className="h-40 animate-pulse rounded-2xl bg-[#F0F4F1]" />
          </div>
        ) : analyzeMutation.isPending ? (
          <div data-testid="analysis-analyzing" className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-[#E2EAE5] bg-white py-16">
            <Loader2 size={28} className="animate-spin text-[#2D5A4C]" />
            <p className="px-6 text-center text-sm text-[#7A9488]">{auto ? t("xp.analyzingAuto") : t("analysis.analyzing")}</p>
          </div>
        ) : !analysis ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-[#C4DFD2] bg-[#F2F7F4] py-14 px-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#2D5A4C] text-white">
              <Sparkles size={26} />
            </div>
            <p className="text-sm text-[#4A6358]">{t("analysis.notAnalyzed")}</p>
            <Button
              data-testid="analysis-analyze-button"
              onClick={() => analyzeMutation.mutate()}
              className="h-12 rounded-xl bg-[#2D5A4C] px-6 font-semibold hover:bg-[#214439]"
            >
              <Sparkles size={16} className="mr-2" /> {t("analysis.analyze")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {analysis.unreadable && (
              <div className="flex items-start gap-3 rounded-2xl border border-[#F8C7C0] bg-[#FDF2F0] p-4">
                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#C03A2B]" />
                <p className="text-sm text-[#C03A2B]">{t("analysis.unreadable")}</p>
              </div>
            )}

            {!analysis.unreadable && (
              <>
                {/* 0. Coherence warning (multi-page) */}
                <PageWarnings warnings={analysis.page_warnings} />
                {/* 1. Human-readable explanation first */}
                <h2 className="font-display text-lg font-extrabold leading-tight text-[#1A2E26]" dir="auto">
                  {loc?.document_type || t("analysis.overview")}
                </h2>

                {(analysis.priority === "URGENT" || analysis.priority === "IMPORTANT") && (
                  <div data-testid={`analysis-priority-${analysis.priority.toLowerCase()}`} className={`flex items-start gap-2 rounded-2xl border p-3 ${analysis.priority === "URGENT" ? "border-[#F5C6C6] bg-[#FDECEC] text-[#C0392B]" : "border-[#FBD9BC] bg-[#FFF3E9] text-[#B36B00]"}`}>
                    <AlertTriangle size={18} className="mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <PriorityBadge priority={analysis.priority} />
                      {loc?.priority_reason && <p className="mt-1 text-sm" dir="auto">{loc.priority_reason}</p>}
                    </div>
                  </div>
                )}

                <ExplanationCard analysis={analysis} loc={loc} loading={locLoading} />

                {/* Contextual mini-Q&A about this document (Premium, max 3 questions) */}
                <MiniQA docId={id} />

                {/* 2. Organize into a case (auto-prepared, editable) */}
                <section data-testid="analysis-organize" className="rounded-2xl border border-[#E2EAE5] bg-white p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <FolderPlus size={16} className="text-[#2D5A4C]" />
                    <h3 className="font-display text-sm font-bold text-[#1A2E26]">{t("xp.organizeTitle")}</h3>
                  </div>
                  <p className="mb-3 text-xs text-[#7A9488]">{t("xp.organizeDesc")}</p>

                  <label className="mb-1 block text-[11px] font-semibold text-[#4A6358]">{t("xp.suggestedTitle")}</label>
                  <Input
                    data-testid="organize-title-input"
                    value={caseTitle}
                    onChange={(e) => setCaseTitle(e.target.value)}
                    dir="auto"
                    className="mb-3 h-11 rounded-xl"
                  />

                  <label className="mb-1 block text-[11px] font-semibold text-[#4A6358]">{t("caseForm.category")}</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger data-testid="organize-category-select" className="mb-3 h-11 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>{t(`categories.${c}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {prefill?.candidates?.length > 0 && (
                    <div data-testid="organize-candidates" className="mb-3 rounded-xl border border-[#C4DFD2] bg-[#F2F7F4] p-3">
                      <p className="mb-2 text-xs font-semibold text-[#2D5A4C]">{t("xp.matchIntro")}</p>
                      {prefill.candidates.map((c) => (
                        <Button
                          key={c.id}
                          data-testid={`organize-attach-${c.id}`}
                          variant="outline"
                          disabled={creating}
                          onClick={() => organize(c.id)}
                          className="mb-2 h-11 w-full rounded-xl border-[#C4DFD2] bg-white text-sm font-semibold text-[#2D5A4C] last:mb-0"
                        >
                          {t("xp.addToExisting").replace("{name}", c.title)}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Button
                    data-testid="organize-create-case"
                    disabled={creating || !caseTitle.trim()}
                    onClick={() => organize(null)}
                    className="h-11 w-full rounded-xl bg-[#2D5A4C] text-sm font-semibold hover:bg-[#214439]"
                  >
                    {creating ? t("xp.creating") : t("xp.createCase")}
                  </Button>
                </section>

                {/* 3. Confirm the important data (human-in-the-loop) */}
                {analysis.critical_items?.length > 0 && (
                  <section data-testid="analysis-critical" className="space-y-2">
                    <h3 className="font-display text-sm font-bold uppercase tracking-wide text-[#4A6358]">{t("analysis.criticalInfo")}</h3>
                    {analysis.critical_items.map((it) => (
                      <CriticalItem key={it.item_id} item={it} analysisId={analysis.id} loc={loc} />
                    ))}
                  </section>
                )}

                {/* 4. Technical details — secondary, collapsible */}
                <section data-testid="analysis-details" className="rounded-2xl border border-[#E2EAE5] bg-white">
                  <button
                    data-testid="analysis-details-toggle"
                    onClick={() => setDetailsOpen((o) => !o)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left"
                  >
                    <span className="font-display text-sm font-bold text-[#4A6358]">{t("xp.detailsSection")}</span>
                    <ChevronDown size={16} className={`text-[#7A9488] transition-transform ${detailsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {detailsOpen && (
                    <div className="border-t border-[#F0F4F1] px-4 py-2">
                      <div className="divide-y divide-[#F0F4F1]">
                        <OverviewRow icon={Building2} label={t("analysis.sender")} value={analysis.sender?.value} />
                        <OverviewRow icon={FileText} label={t("analysis.docType")} value={analysis.document_type?.value} />
                        <OverviewRow icon={CalendarDays} label={t("analysis.docDate")} value={analysis.document_date?.value} />
                      </div>
                      {analysis.summary && (
                        <p className="mt-2 border-t border-[#F0F4F1] pt-2 text-sm text-[#4A6358]" dir="auto">{analysis.summary}</p>
                      )}
                      <p className="mt-3 text-[10px] text-[#B0C2B8]">{t("analysis.provider")}</p>
                    </div>
                  )}
                </section>
              </>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                data-testid="analysis-manual-case"
                onClick={() => navigate(`/cases/new?from=${id}`)}
                className="text-xs font-semibold text-[#7A9488]"
              >
                {t("xp.manualEntry")}
              </button>
              <button
                data-testid="analysis-reanalyze"
                onClick={() => analyzeMutation.mutate()}
                className="text-xs font-semibold text-[#2D5A4C]"
              >
                {t("analysis.reanalyze")}
              </button>
            </div>
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
